export const FIELD_SCHEMA_VERSION = 1 as const;

// Primitive field types carry no type-specific required props beyond BaseFormField.
export type PrimitiveFormFieldType = 'single_line_text' | 'paragraph_text' | 'date' | 'checkbox' | 'signature';

export type FormFieldType =
  | PrimitiveFormFieldType
  | 'multiple_choice'
  | 'dropdown'
  | 'section_divider'
  | 'merge_field' // standalone read-only merge-field display block
  | 'rich_text'; // read-only formatted content block (e.g. T&Cs) — see RichTextFormField

export interface FieldOption {
  id: string; // uuid (crypto.randomUUID)
  label: string;
  value: string; // defaults to label if not edited
}

// Inline merge-field token embedded inside label/help_text/paragraph default content.
// Tokens are stored in text as a sentinel the editor parses: {{merge:client.fullName}}
// Rendered as a styled, non-editable chip; never shows raw token to the user.
export type InlineTokenString = string; // contains zero+ {{merge:<token>}} sequences

export interface BaseFormField {
  id: string; // uuid, stable across reorder/persist
  label: InlineTokenString; // may contain inline merge tokens
  help_text?: InlineTokenString;
  // Prefilled / default value for the field. May contain inline {{merge:<token>}} sentinels so that
  // parts of the value are populated from a merge field (e.g. "Dear {{merge:client.fullName}},"). Per type:
  // text/paragraph → text, date → ISO date string, checkbox → 'true'|'false', choice → selected option value.
  default_value?: InlineTokenString;
  required: boolean;
  read_only: boolean;
  include_in_pdf: boolean; // consumed by TAV-2662
  conditional_logic: null; // reserved/stubbed — always null this ticket
}

export interface PrimitiveFormField extends BaseFormField {
  type: PrimitiveFormFieldType;
}

export interface ChoiceFormField extends BaseFormField {
  type: 'multiple_choice' | 'dropdown';
  options: FieldOption[];
  allow_multiple?: boolean; // multiple_choice only
}

export interface MergeDisplayFormField extends BaseFormField {
  type: 'merge_field';
  read_only: true; // always read-only
  merge_token: string; // e.g. 'client.fullName' (from registry .token)
}

export interface SectionDividerFormField extends BaseFormField {
  type: 'section_divider';
  // label reused as the section heading; required/read_only ignored at render time
}

export interface RichTextFormField extends BaseFormField {
  type: 'rich_text';
  read_only: true; // always read-only — static formatted content shown to the filler (e.g. T&Cs)
  content: string; // sanitised HTML authored in the Tiptap editor; rendered read-only in the filler view
  // label/help_text/default_value/required are unused — the block *is* its content
}

export type FormField =
  | PrimitiveFormField // single_line_text, paragraph_text, date, checkbox, signature
  | ChoiceFormField
  | MergeDisplayFormField
  | SectionDividerFormField
  | RichTextFormField;

export interface FieldSchema {
  version: typeof FIELD_SCHEMA_VERSION;
  fields: FormField[];
}
